import React, { useMemo, useState, useCallback, useRef, useEffect } from 'react';
import {
  Box, Typography, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField,
  Tooltip, Drawer, Divider, IconButton, InputAdornment, alpha,
} from '@mui/material';
import {
  Close as CloseIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  CalendarMonth as CalendarMonthIcon,
} from '@mui/icons-material';
import { motion, AnimatePresence } from 'framer-motion';

import { TableListPagination } from '../../common/TableList';
import { tableListThSx, tableNavButtonSx } from '../../common/SaaSTokens';
import {
  C, R, FONT_UI, FONT_MONO, FONT_TITLE, TRANSITION,
  getStatusMeta, normalizeAttendanceDate, inputSx,
  UserAvatarCell,
  DrawerBadge,
  AttendanceTableShell,
  AttendanceStatusLegend,
} from './AttendanceSharedUI';
import { formatMalaysiaAttendanceClock } from '../../../utils/malaysiaTime';

const MotionBox = motion(Box);

// ─── Design tokens ─────────────────────────────────────────────────────────────
const B = {
  400: '#60A5FA', 500: '#3B82F6', 600: '#2563EB',
};

// Status color palette — matches DailyAttendance
const STATUS_COLORS = {
  present:        '#10b981',
  approved_absent:'#f59e0b',
  absent:         '#ef4444',
  no_record:      '#64748b',
  default:        '#334155',
};

// Per-user avatar palettes
const AVATAR_PALETTES = [
  { bg: alpha('#1e40af', 0.18), color: '#93c5fd', border: alpha('#1e40af', 0.35) },
  { bg: alpha('#7c3aed', 0.18), color: '#c4b5fd', border: alpha('#7c3aed', 0.35) },
  { bg: alpha('#0f766e', 0.18), color: '#5eead4', border: alpha('#0f766e', 0.35) },
  { bg: alpha('#b45309', 0.18), color: '#fcd34d', border: alpha('#b45309', 0.35) },
  { bg: alpha('#9d174d', 0.18), color: '#f9a8d4', border: alpha('#9d174d', 0.35) },
  { bg: alpha('#065f46', 0.18), color: '#6ee7b7', border: alpha('#065f46', 0.35) },
  { bg: alpha('#1e3a8a', 0.18), color: '#a5b4fc', border: alpha('#1e3a8a', 0.35) },
  { bg: alpha('#7f1d1d', 0.18), color: '#fca5a5', border: alpha('#7f1d1d', 0.35) },
];

function getInitials(name = '') {
  return name.split(' ').map((n) => n[0]).join('').substring(0, 2).toUpperCase();
}

// ─── Colourful avatar ──────────────────────────────────────────────────────────
const ColorAvatar = ({ user, index, size = 32 }) => {
  const p = AVATAR_PALETTES[index % AVATAR_PALETTES.length];
  return (
    <Box sx={{
      width: size, height: size,
      borderRadius: '10px',
      background: p.bg,
      border: `1.5px solid ${p.border}`,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      flexShrink: 0,
      color: p.color,
      fontSize: size * 0.33,
      fontWeight: 700,
      fontFamily: FONT_UI,
      letterSpacing: '0.5px',
    }}>
      {getInitials(user?.name || user?.full_name || '')}
    </Box>
  );
};

// ─── Summary pill (sticky right columns) ──────────────────────────────────────
const SummaryPill = ({ value, color }) => (
  <Box sx={{
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
    minWidth: 34, px: '8px', py: '4px',
    borderRadius: '999px',
    bgcolor: alpha(color, 0.14),
    color: color,
    border: `1px solid ${alpha(color, 0.35)}`,
    fontSize: 12, fontWeight: 700, fontFamily: FONT_MONO,
    letterSpacing: '0.3px',
  }}>
    {value}
  </Box>
);

// ─── Individual day cell ───────────────────────────────────────────────────────
const DayCell = React.memo(({ attendance, dateStr, canUpdate, onCycle, dimmed, isWeekend }) => {
  const meta = getStatusMeta(attendance);
  const hasRecord = meta.type !== 'no_record';

  // Richer status colors — only dim when a stat filter hides this cell, not on weekends.
  const statusColor = STATUS_COLORS[meta.type] || STATUS_COLORS.default;

  const tooltipContent = (
    <Box sx={{ p: 0.75, minWidth: 156 }}>
      <Typography sx={{
        fontSize: 12, fontWeight: 700, color: '#f1f5f9', mb: 0.5, fontFamily: FONT_UI,
      }}>
        {new Date(dateStr + 'T12:00:00').toLocaleDateString('en-US', {
          weekday: 'short', month: 'short', day: 'numeric',
        })}
      </Typography>
      <Box sx={{
        display: 'inline-flex', alignItems: 'center', gap: '5px',
        px: '8px', py: '2px', mb: 0.75,
        borderRadius: '999px',
        bgcolor: dimmed ? 'rgba(51,65,85,0.3)' : alpha(statusColor, 0.18),
        border: `1px solid ${dimmed ? 'rgba(51,65,85,0.4)' : alpha(statusColor, 0.4)}`,
      }}>
        <Box sx={{
          width: 5, height: 5, borderRadius: '50%', flexShrink: 0,
          bgcolor: dimmed ? '#334155' : statusColor,
        }} />
        <Typography sx={{
          fontSize: 11, fontWeight: 700, fontFamily: FONT_UI,
          color: dimmed ? '#475569' : statusColor,
        }}>
          {meta.label}
        </Typography>
      </Box>
      <Divider sx={{ borderColor: 'rgba(255,255,255,0.07)', my: 0.75 }} />
      <Typography sx={{ fontSize: 11, color: 'rgba(148,163,184,0.85)', fontFamily: FONT_MONO, lineHeight: 1.7 }}>
        In:  {attendance?.check_in_time ? formatMalaysiaAttendanceClock(attendance.check_in_time) : '—'}{'\n'}
        Out: {attendance?.check_out_time ? formatMalaysiaAttendanceClock(attendance.check_out_time) : '—'}
      </Typography>
      {attendance?.absence_reason && (
        <>
          <Divider sx={{ borderColor: 'rgba(255,255,255,0.07)', my: 0.75 }} />
          <Typography sx={{ fontSize: 11, color: '#f1f5f9', fontFamily: FONT_UI, lineHeight: 1.5 }}>
            {attendance.absence_reason}
          </Typography>
        </>
      )}
    </Box>
  );

  const dotColor  = dimmed ? '#64748b'             : statusColor;
  const dotBg     = dimmed ? alpha('#1e293b', 0.4) : alpha(statusColor, 0.16);
  const dotBorder = dimmed ? alpha('#64748b', 0.5) : alpha(statusColor, 0.45);
  const cellBg    = dimmed
    ? 'transparent'
    : hasRecord
      ? alpha(statusColor, 0.07)
      : isWeekend
        ? alpha('#ef4444', 0.04)
        : alpha(statusColor, 0.07);

  return (
    <Tooltip
      title={tooltipContent}
      arrow
      placement="top"
      componentsProps={{
        tooltip: {
          sx: {
            bgcolor: 'rgba(9,15,28,0.98)',
            border: '1px solid rgba(255,255,255,0.1)',
            borderRadius: '10px',
            backdropFilter: 'blur(16px)',
            p: 1,
            maxWidth: 220,
          },
        },
        arrow: { sx: { color: 'rgba(9,15,28,0.98)' } },
      }}
    >
      <TableCell
        onClick={(e) => { e.stopPropagation(); if (canUpdate) onCycle?.(); }}
        align="center"
        sx={{
          width: 36, minWidth: 36, maxWidth: 36, height: 44,
          p: '3px',
          border: '1px solid rgba(255,255,255,0.04)',
          bgcolor: cellBg,
          cursor: canUpdate ? 'pointer' : 'default',
          transition: 'all 0.12s ease',
          '&:hover': canUpdate ? {
            bgcolor: alpha(statusColor, 0.2),
            '& .day-dot': { transform: 'scale(1.18)' },
          } : {},
        }}
      >
        <Box
          className="day-dot"
          sx={{
            width: 24, height: 24, m: 'auto',
            borderRadius: '50%',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 11, fontWeight: 800, lineHeight: 1,
            color: dotColor,
            bgcolor: dotBg,
            border: `1.5px solid ${dotBorder}`,
            transition: 'transform 0.12s ease',
          }}
        >
          {meta.symbol}
        </Box>
      </TableCell>
    </Tooltip>
  );
});

// ─── MonthlyAttendanceModern ───────────────────────────────────────────────────
const MonthlyAttendanceModern = ({
  users = [], attendanceData = [], yearlyAttendanceData = [],
  currentMonth, permissions,
  onMonthChange, onSelectMonth, onAttendanceClick, statusFilter = 'all',
}) => {
  const [drawerUser, setDrawerUser] = useState(null);
  const [page,        setPage]       = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const monthInputRef = useRef(null);

  const daysInMonth = useMemo(
    () => new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 0).getDate(),
    [currentMonth],
  );

  const monthTitle = useMemo(
    () => currentMonth.toLocaleDateString('en-US', { year: 'numeric', month: 'long' }),
    [currentMonth],
  );

  const yearMonth = useMemo(
    () => `${currentMonth.getFullYear()}-${String(currentMonth.getMonth() + 1).padStart(2, '0')}`,
    [currentMonth],
  );

  useEffect(() => { setPage(0); }, [users, yearMonth]);

  const attIndex = useMemo(() => {
    const idx = new Map();
    attendanceData.forEach((r) => {
      const dateKey = normalizeAttendanceDate(r.date);
      if (!dateKey) return;
      idx.set(`${r.user_id}::${dateKey}`, r);
    });
    return idx;
  }, [attendanceData]);

  const getAtt = useCallback((uid, dateStr) => attIndex.get(`${uid}::${dateStr}`) || null, [attIndex]);

  const yearlyDeductionByUser = useMemo(() => {
    const out = new Map();
    const year      = currentMonth.getFullYear();
    const yearStart = `${year}-01-01`;
    const yearEnd   = `${year}-12-31`;
    yearlyAttendanceData.forEach((rec) => {
      if (!rec?.user_id || !rec?.date) return;
      if (rec.date < yearStart || rec.date > yearEnd) return;
      const cur = out.get(rec.user_id) || { approvedDeduct: 0, absentDeduct: 0 };
      if (rec.status === 'approved_absent') cur.approvedDeduct += 1;
      else if (rec.status === 'absent' || (!rec.status && !rec.check_in_time)) cur.absentDeduct += 1;
      out.set(rec.user_id, cur);
    });
    return out;
  }, [yearlyAttendanceData, currentMonth]);

  const counts = useMemo(() => {
    const out = new Map();
    users.forEach((u) => {
      let present = 0, approved = 0, absent = 0, noRecord = 0;
      for (let d = 1; d <= daysInMonth; d++) {
        const dateStr = `${yearMonth}-${String(d).padStart(2, '0')}`;
        const rec     = getAtt(u.id, dateStr);
        const meta    = getStatusMeta(rec);
        if      (meta.type === 'present')         present  += 1;
        else if (meta.type === 'approved_absent') approved += 1;
        else if (meta.type === 'absent')          absent   += 1;
        else                                      noRecord += 1;
      }
      const yearly    = yearlyDeductionByUser.get(u.id) || { approvedDeduct: 0, absentDeduct: 0 };
      const deduction = yearly.approvedDeduct + yearly.absentDeduct * 3;
      out.set(u.id, { present, approved, absent, noRecord, deduction, remain: 14 - deduction });
    });
    return out;
  }, [users, daysInMonth, yearMonth, getAtt, yearlyDeductionByUser]);

  const canUpdate = permissions?.canUpdateStatus?.() ?? false;

  const paginated = useMemo(
    () => users.slice(page * rowsPerPage, (page + 1) * rowsPerPage),
    [users, page, rowsPerPage],
  );

  const LEFT_W  = 220;
  const DAY_W   = 36;
  const RIGHT_W = 88;

  // ── Shared header cell styles ─────────────────────────────────────────────
  const thSx = {
    ...tableListThSx,
    py: 1.5,
    px: 0,
    fontSize: '0.7rem',
    letterSpacing: '0.6px',
    position: 'sticky',
    top: 0,
    zIndex: 6,
  };

  // ── Sticky col styles ─────────────────────────────────────────────────────
  const BG_ROW_EVEN = 'rgba(13,26,46,1)';
  const BG_ROW_ODD  = 'rgba(10,20,35,1)';
  const BG_HEADER   = 'rgba(9,15,28,1)';

  const stickyLeftSx = {
    position: 'sticky', left: 0, zIndex: 5,
    bgcolor: BG_ROW_EVEN,
    borderRight: '1px solid rgba(255,255,255,0.07)',
  };

  const stickyRightSx = (right, w) => ({
    position: 'sticky', right, zIndex: 5,
    bgcolor: BG_ROW_EVEN,
    borderLeft: '1px solid rgba(255,255,255,0.06)',
    width: w, minWidth: w,
  });

  const du = drawerUser ? counts.get(drawerUser.id) : null;

  // ── Day-of-week label helpers ─────────────────────────────────────────────
  const dayOfWeek = (d) => {
    const day = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), d).getDay();
    return day === 0 || day === 6; // weekend
  };

  // ── Month navigator ───────────────────────────────────────────────────────
  const monthNavigator = (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
      <Tooltip title="Previous month" arrow>
        <span>
          <IconButton size="small" onClick={() => onMonthChange(-1)} sx={tableNavButtonSx(false)}>
            <ChevronLeftIcon />
          </IconButton>
        </span>
      </Tooltip>

      <TextField
        inputRef={monthInputRef}
        type="month"
        size="small"
        value={yearMonth}
        onChange={(e) => onSelectMonth?.(e.target.value)}
        InputLabelProps={{ shrink: true }}
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              <IconButton
                size="small"
                onClick={() => {
                  const el = monthInputRef.current;
                  if (!el) return;
                  if (typeof el.showPicker === 'function') el.showPicker();
                  else el.focus();
                }}
                sx={{
                  color: alpha('#E2E8F0', 0.7),
                  '&:hover': { color: '#fff', bgcolor: alpha('#E2E8F0', 0.08) },
                }}
              >
                <CalendarMonthIcon sx={{ fontSize: 16 }} />
              </IconButton>
            </InputAdornment>
          ),
        }}
        sx={{
          ...inputSx,
          width: 148,
          '& .MuiInputBase-input': { colorScheme: 'dark', fontSize: 12, fontFamily: FONT_MONO },
          '& input[type="month"]::-webkit-calendar-picker-indicator': {
            opacity: 0, position: 'absolute', right: 0, width: '100%', height: '100%', cursor: 'pointer',
          },
        }}
      />

      <Tooltip title="Next month" arrow>
        <span>
          <IconButton size="small" onClick={() => onMonthChange(1)} sx={tableNavButtonSx(false)}>
            <ChevronRightIcon />
          </IconButton>
        </span>
      </Tooltip>
    </Box>
  );

  return (
    <AttendanceTableShell
      streamLabel="Monthly Overview"
      subtitle={canUpdate ? `${monthTitle} — click any day cell to cycle status` : monthTitle}
      recordCount={users.length}
      headerRight={monthNavigator}
    >
      <TableContainer sx={{
        width: '100%',
        overflowX: 'auto',
        '&::-webkit-scrollbar': { height: 6 },
        '&::-webkit-scrollbar-thumb': { background: alpha('#38bdf8', 0.28), borderRadius: 4 },
        '&::-webkit-scrollbar-track': { background: 'transparent' },
      }}>
        <Table
          stickyHeader
          sx={{
            tableLayout: 'fixed',
            width: 'max-content',
            borderCollapse: 'separate',
            borderSpacing: 0,
          }}
        >
          <colgroup>
            <col style={{ width: LEFT_W }} />
            {Array.from({ length: daysInMonth }).map((_, i) => (
              <col key={i} style={{ width: DAY_W }} />
            ))}
            <col style={{ width: RIGHT_W }} />
            <col style={{ width: RIGHT_W }} />
            <col style={{ width: RIGHT_W }} />
          </colgroup>

          <TableHead>
            <TableRow>
              {/* Member header */}
              <TableCell sx={{
                ...thSx,
                ...stickyLeftSx,
                width: LEFT_W,
                px: '20px',
                zIndex: 7,
                bgcolor: BG_HEADER,
                color: 'rgba(148,163,184,0.9)',
              }}>
                Member
              </TableCell>

              {/* Day number headers */}
              {Array.from({ length: daysInMonth }).map((_, i) => {
                const d    = i + 1;
                const isWE = dayOfWeek(d);
                return (
                  <TableCell
                    key={d}
                    align="center"
                    sx={{
                      ...thSx,
                      width: DAY_W,
                      px: 0,
                      fontSize: 10,
                      color: isWE ? 'rgba(248,113,113,0.6)' : 'rgba(148,163,184,0.7)',
                      bgcolor: isWE ? alpha('#ef4444', 0.04) : BG_HEADER,
                    }}
                  >
                    {d}
                  </TableCell>
                );
              })}

              {/* Summary column headers */}
              {[
                { label: 'Total',  color: '#10b981', off: RIGHT_W * 2 },
                { label: 'Deduct', color: '#f59e0b', off: RIGHT_W     },
                { label: 'Remain', color: B[400],    off: 0           },
              ].map(({ label, color, off }) => (
                <TableCell
                  key={label}
                  align="center"
                  sx={{
                    ...thSx,
                    ...stickyRightSx(off, RIGHT_W),
                    zIndex: 7,
                    bgcolor: BG_HEADER,
                    color,
                  }}
                >
                  {label}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>

          <TableBody>
            {users.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={daysInMonth + 4}
                  sx={{ border: 'none', py: 10, bgcolor: BG_ROW_EVEN }}
                >
                  <Typography sx={{
                    textAlign: 'center', color: 'rgba(100,116,139,0.8)',
                    fontSize: 13, fontFamily: FONT_UI,
                  }}>
                    No members match your filters.
                  </Typography>
                </TableCell>
              </TableRow>
            ) : paginated.map((user, idx) => {
              const absIdx = page * rowsPerPage + idx;
              const c      = counts.get(user.id);
              const remain = c?.remain ?? 0;
              const rowBg  = absIdx % 2 === 0 ? BG_ROW_EVEN : BG_ROW_ODD;

              return (
                <TableRow
                  key={user.id}
                  onClick={() => setDrawerUser(user)}
                  sx={{
                    cursor: 'pointer',
                    transition: 'background 0.12s',
                    '&:hover': { bgcolor: alpha('#38bdf8', 0.05) },
                    '&:hover .sticky-left-cell':  { bgcolor: 'rgba(16,30,56,1)' },
                    '&:hover .sticky-right-cell': { bgcolor: 'rgba(16,30,56,1)' },
                    '& .MuiTableCell-root': {
                      borderBottom: `1px solid rgba(255,255,255,0.04)`,
                    },
                  }}
                >
                  {/* Sticky left — member */}
                  <TableCell
                    className="sticky-left-cell"
                    sx={{
                      ...stickyLeftSx,
                      width: LEFT_W,
                      py: 1.5,
                      px: '20px',
                      bgcolor: rowBg,
                      transition: 'background 0.12s',
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                      <ColorAvatar user={user} index={absIdx} size={32} />
                      <Box sx={{ overflow: 'hidden' }}>
                        <Typography sx={{
                          fontSize: 13, fontWeight: 600, color: '#f1f5f9',
                          fontFamily: FONT_UI, lineHeight: 1.25,
                          whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                        }}>
                          {user.name || user.full_name}
                        </Typography>
                        {user.department?.name && (
                          <Typography sx={{
                            fontSize: 10.5, color: 'rgba(100,116,139,0.85)',
                            fontFamily: FONT_UI, mt: 0.25,
                            whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                          }}>
                            {user.department.name}
                          </Typography>
                        )}
                      </Box>
                    </Box>
                  </TableCell>

                  {/* Day cells */}
                  {Array.from({ length: daysInMonth }).map((_, i) => {
                    const d       = i + 1;
                    const dateStr = `${yearMonth}-${String(d).padStart(2, '0')}`;
                    const att     = getAtt(user.id, dateStr);
                    const meta    = getStatusMeta(att);
                    const isWE    = dayOfWeek(d);

                    return (
                      <DayCell
                        key={`${user.id}-${d}`}
                        attendance={att}
                        dateStr={dateStr}
                        canUpdate={canUpdate}
                        onCycle={() => onAttendanceClick?.(user.id, dateStr)}
                        dimmed={
                          statusFilter !== 'all' && (
                            (statusFilter === 'present'   && meta.type !== 'present') ||
                            (statusFilter === 'absent'    && !['approved_absent', 'absent'].includes(meta.type)) ||
                            (statusFilter === 'no_record' && meta.type !== 'no_record')
                          )
                        }
                        isWeekend={isWE}
                      />
                    );
                  })}

                  {/* Summary sticky right */}
                  {[
                    { v: c?.present   ?? 0, color: '#10b981',                         off: RIGHT_W * 2, w: RIGHT_W },
                    { v: c?.deduction ?? 0, color: '#f59e0b',                         off: RIGHT_W,     w: RIGHT_W },
                    { v: remain,            color: remain < 5 ? '#ef4444' : '#10b981', off: 0,           w: RIGHT_W },
                  ].map(({ v, color, off, w }, ci) => (
                    <TableCell
                      key={ci}
                      align="center"
                      className="sticky-right-cell"
                      sx={{
                        ...stickyRightSx(off, w),
                        py: 1.5,
                        bgcolor: rowBg,
                        transition: 'background 0.12s',
                      }}
                    >
                      <SummaryPill value={v} color={color} />
                    </TableCell>
                  ))}
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      <TableListPagination
        page={page}
        rowsPerPage={rowsPerPage}
        totalCount={users.length}
        onPageChange={(_, p) => setPage(p)}
        onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
      />

      <Box sx={{ px: { xs: 2, sm: 3 }, pb: 2.5, pt: 0.5 }}>
        <AttendanceStatusLegend visible={canUpdate} />
      </Box>

      {/* ── Detail drawer ── */}
      <Drawer
        anchor="right"
        open={Boolean(drawerUser)}
        onClose={() => setDrawerUser(null)}
        PaperProps={{
          sx: {
            width: { xs: '100%', sm: 340 },
            background: 'rgba(11,17,32,0.98)',
            borderLeft: '1px solid rgba(255,255,255,0.08)',
            backdropFilter: 'blur(24px)',
          },
        }}
      >
        <AnimatePresence>
          {drawerUser && du && (
            <MotionBox
              initial={{ opacity: 0, x: 16 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.18 }}
              sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}
            >
              {/* Drawer header */}
              <Box sx={{
                px: 3, py: 2.5,
                borderBottom: '1px solid rgba(255,255,255,0.08)',
                display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between',
                background: 'linear-gradient(135deg, rgba(59,130,246,0.06) 0%, rgba(167,139,250,0.04) 100%)',
              }}>
                <Box>
                  <Typography sx={{ fontSize: 14, fontWeight: 700, color: '#f1f5f9', fontFamily: FONT_UI }}>
                    Monthly Summary
                  </Typography>
                  <Typography sx={{ fontSize: 11, color: 'rgba(100,116,139,1)', fontFamily: FONT_UI, mt: 0.3 }}>
                    {monthTitle}
                  </Typography>
                </Box>
                <IconButton
                  size="small"
                  onClick={() => setDrawerUser(null)}
                  sx={{
                    width: 28, height: 28, borderRadius: '8px',
                    border: '1px solid rgba(255,255,255,0.1)',
                    bgcolor: 'rgba(30,41,59,0.6)',
                    color: 'rgba(148,163,184,0.8)',
                    '&:hover': { color: '#f1f5f9', bgcolor: 'rgba(30,41,59,0.9)' },
                  }}
                >
                  <CloseIcon sx={{ fontSize: 13 }} />
                </IconButton>
              </Box>

              {/* Drawer body */}
              <Box sx={{ p: 3, display: 'flex', flexDirection: 'column', overflowY: 'auto' }}>
                {/* Avatar + name */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.75, mb: 3 }}>
                  <ColorAvatar user={drawerUser} index={users.indexOf(drawerUser)} size={48} />
                  <Box>
                    <Typography sx={{ fontSize: 15, fontWeight: 700, color: '#f1f5f9', fontFamily: FONT_UI }}>
                      {drawerUser.name || drawerUser.full_name}
                    </Typography>
                    {drawerUser.department?.name && (
                      <Typography sx={{ fontSize: 11.5, color: 'rgba(100,116,139,0.9)', fontFamily: FONT_UI, mt: 0.3 }}>
                        {drawerUser.department.name}
                      </Typography>
                    )}
                  </Box>
                </Box>

                <Divider sx={{ borderColor: 'rgba(255,255,255,0.07)', mb: 2.5 }} />

                {/* This Month stats */}
                <Typography sx={{
                  fontSize: 10, fontWeight: 800, color: 'rgba(100,116,139,0.85)',
                  fontFamily: FONT_UI, textTransform: 'uppercase', letterSpacing: '1px', mb: 1.25,
                }}>
                  This Month
                </Typography>

                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1, mb: 2.5 }}>
                  {[
                    { label: 'Present',   value: du.present,  color: '#10b981' },
                    { label: 'Approved',  value: du.approved, color: '#f59e0b' },
                    { label: 'Absent',    value: du.absent,   color: '#ef4444' },
                    { label: 'No record', value: du.noRecord, color: '#64748b' },
                  ].map(({ label, value, color }) => (
                    <Box key={label} sx={{
                      display: 'flex', flexDirection: 'column', gap: 0.5,
                      px: 1.75, py: 1.5,
                      borderRadius: '10px',
                      bgcolor: alpha(color, 0.1),
                      border: `1px solid ${alpha(color, 0.25)}`,
                    }}>
                      <Typography sx={{
                        fontSize: 9.5, fontWeight: 800, letterSpacing: '0.6px',
                        color: alpha(color, 0.8),
                        textTransform: 'uppercase', fontFamily: FONT_UI,
                      }}>
                        {label}
                      </Typography>
                      <Typography sx={{
                        fontSize: 22, fontWeight: 700, color,
                        fontFamily: FONT_MONO, lineHeight: 1,
                      }}>
                        {value}
                      </Typography>
                    </Box>
                  ))}
                </Box>

                <Divider sx={{ borderColor: 'rgba(255,255,255,0.07)', mb: 2.5 }} />

                {/* Absence reasons */}
                <Typography sx={{
                  fontSize: 10, fontWeight: 800, color: 'rgba(100,116,139,0.85)',
                  fontFamily: FONT_UI, textTransform: 'uppercase', letterSpacing: '1px', mb: 1.25,
                }}>
                  Absence Reasons
                </Typography>

                <Box sx={{
                  bgcolor: 'rgba(15,23,42,0.6)',
                  border: '1px solid rgba(255,255,255,0.07)',
                  borderRadius: '10px',
                  overflow: 'hidden',
                  mb: 2.5,
                  maxHeight: 160,
                  overflowY: 'auto',
                }}>
                  {attendanceData
                    .filter((r) => r.user_id === drawerUser.id && r.absence_reason)
                    .sort((a, b) => String(b.date).localeCompare(String(a.date)))
                    .slice(0, 5)
                    .map((r, i, arr) => (
                      <Box
                        key={`${r.user_id}-${r.date}`}
                        sx={{
                          px: 2, py: 1.25,
                          borderBottom: i < arr.length - 1 ? '1px solid rgba(255,255,255,0.05)' : 'none',
                          display: 'flex', gap: 1.25, alignItems: 'flex-start',
                        }}
                      >
                        <Typography sx={{
                          fontSize: 10.5, color: 'rgba(100,116,139,0.8)',
                          fontFamily: FONT_MONO, whiteSpace: 'nowrap', mt: 0.15,
                        }}>
                          {r.date}
                        </Typography>
                        <Typography sx={{ fontSize: 11.5, color: '#f1f5f9', fontFamily: FONT_UI, lineHeight: 1.5 }}>
                          {r.absence_reason}
                        </Typography>
                      </Box>
                    ))}
                  {!attendanceData.some((r) => r.user_id === drawerUser.id && r.absence_reason) && (
                    <Box sx={{ px: 2, py: 1.5 }}>
                      <Typography sx={{ fontSize: 11.5, color: 'rgba(100,116,139,0.7)', fontFamily: FONT_UI }}>
                        No absence reason recorded.
                      </Typography>
                    </Box>
                  )}
                </Box>

                <Divider sx={{ borderColor: 'rgba(255,255,255,0.07)', mb: 2.5 }} />

                {/* Leave balance */}
                <Typography sx={{
                  fontSize: 10, fontWeight: 800, color: 'rgba(100,116,139,0.85)',
                  fontFamily: FONT_UI, textTransform: 'uppercase', letterSpacing: '1px', mb: 1.25,
                }}>
                  Leave Balance
                </Typography>

                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1 }}>
                  {[
                    { label: 'Deductions',   value: du.deduction, color: '#f59e0b' },
                    { label: 'Leave Remain', value: du.remain,    color: du.remain < 5 ? '#ef4444' : '#10b981' },
                  ].map(({ label, value, color }) => (
                    <Box key={label} sx={{
                      display: 'flex', flexDirection: 'column', gap: 0.5,
                      px: 1.75, py: 1.5,
                      borderRadius: '10px',
                      bgcolor: alpha(color, 0.1),
                      border: `1px solid ${alpha(color, 0.25)}`,
                    }}>
                      <Typography sx={{
                        fontSize: 9.5, fontWeight: 800, letterSpacing: '0.6px',
                        color: alpha(color, 0.8),
                        textTransform: 'uppercase', fontFamily: FONT_UI,
                      }}>
                        {label}
                      </Typography>
                      <Typography sx={{
                        fontSize: 22, fontWeight: 700, color,
                        fontFamily: FONT_MONO, lineHeight: 1,
                      }}>
                        {value}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              </Box>
            </MotionBox>
          )}
        </AnimatePresence>
      </Drawer>
    </AttendanceTableShell>
  );
};

export default MonthlyAttendanceModern;